import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Section, SectionHeading, Panel, Reveal, Eyebrow } from '../components/ui';
import { FONT, TYPE } from '../theme/tokens';

export default function About() {
  const { t } = useTranslation();

  // `returnObjects` întoarce direct tabloul din fișierul de traduceri. Dacă o
  // cheie lipsește, i18next întoarce chiar cheia (un șir), nu un tablou — de
  // aceea lista e verificată înainte de `map`.
  const spec = (() => {
    const value = t('about.spec', { returnObjects: true });
    return Array.isArray(value) ? value : [];
  })();

  return (
    <>
      {/* ────────────────────────  Teza + fișa firmei  ──────────────────── */}
      <Section first component="header">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(320px, .9fr)' },
            gap: { xs: 5, md: 8 },
            alignItems: 'start',
          }}
        >
          <SectionHeading
            component="h1"
            eyebrow={t('about.eyebrow')}
            title={t('about.title')}
            lede={t('about.description')}
            titleSx={TYPE.h1}
            maxWidth="none"
          />

          <Reveal delay={0.12}>
            <Panel
              variant="glass"
              component="dl"
              header={{ title: t('about.blockTitle'), meta: t('about.blockRev') }}
              sx={{ m: 0 }}
            >
              {spec.map((row) => (
                <Box
                  key={row.k}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '104px 1fr', sm: '128px 1fr' },
                    borderTop: '1px solid var(--qf-glass-line)',
                    '&:first-of-type': { borderTop: 'none' },
                  }}
                >
                  <Box
                    component="dt"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: { xs: 2, sm: 2.75 },
                      py: 1.75,
                      fontFamily: FONT.mono,
                      fontSize: 11.5,
                      letterSpacing: '.08em',
                      textTransform: 'uppercase',
                      color: 'var(--qf-ink-3)',
                      borderRight: '1px solid var(--qf-glass-line)',
                      // Etichetele mono nu se rup singure la 104px pe telefon.
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {row.k}
                  </Box>
                  <Box
                    component="dd"
                    sx={{
                      m: 0,
                      px: { xs: 2, sm: 2.75 },
                      py: 1.75,
                      ...TYPE.small,
                      color: 'var(--qf-ink)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {row.v}
                  </Box>
                </Box>
              ))}
            </Panel>
          </Reveal>
        </Box>
      </Section>

      {/* ─────────────────────────  Invitația  ──────────────────────────── */}
      <Section tone="deep">
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
          <Reveal>
            <Eyebrow onDark sx={{ mb: 2 }}>
              {t('home.ctaEyebrow')}
            </Eyebrow>
            <Typography component="h2" sx={{ ...TYPE.h2, color: 'var(--qf-deep-ink)', textWrap: 'balance', m: 0 }}>
              {t('about.ctaTitle')}
            </Typography>
          </Reveal>

          <Reveal
            delay={0.1}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: 1.5,
              mt: { xs: 4, md: 5 },
            }}
          >
            <Button component={Link} to="/contact" variant="onDarkFilled" size="large">
              {t('about.ctaSecondary')}
            </Button>
            <Button component={Link} to="/services" variant="onDark" size="large">
              {t('about.ctaPrimary')}
            </Button>
          </Reveal>
        </Box>
      </Section>
    </>
  );
}
